import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { verifyEmail, resendEmailVerification } from '../api/auth'
import { CheckCircle, XCircle, Mail, RefreshCw } from 'lucide-react'

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isResending, setIsResending] = useState(false)
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')
  const hasVerified = useRef(false)

  const email = searchParams.get('email')
  const token = searchParams.get('token')

  useEffect(() => {
    if (hasVerified.current) return
    
    const verifyEmailAsync = async () => {
      if (!email || !token) {
        setStatus('error')
        setMessage('Invalid verification link. Please check your email or request a new verification email.')
        return
      }

      hasVerified.current = true

      try {
        const response = await verifyEmail(email, token)
        setStatus('success')
        setMessage(response.message)
        
        queryClient.invalidateQueries({ queryKey: ['me'] })
        
        setTimeout(() => {
          window.location.href = '/';
        }, 3000)
      } catch (error: unknown) {
        setStatus('error')
        const errorMessage = error instanceof Error ? error.message : 'Email verification failed. The link may have expired.'
        setMessage(errorMessage)
      }
    }

    verifyEmailAsync()
  }, [email, token, queryClient, navigate])

  const handleResendVerification = async () => {
    if (!email) return
    
    setIsResending(true)
    try {
      await resendEmailVerification(email)
      setMessage('A new verification email has been sent. Please check your inbox.')
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend verification email. Please try again.'
      setMessage(errorMessage)
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6">
            {status === 'loading' ? (
              <RefreshCw className="h-8 w-8 text-brand-600 animate-spin" />
            ) : status === 'success' ? (
              <CheckCircle className="h-8 w-8 text-success-600" />
            ) : (
              <XCircle className="h-8 w-8 text-danger-600" />
            )}
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {status === 'loading' ? 'Verifying your email...' : 
             status === 'success' ? 'Email verified!' : 
             'Verification failed'}
          </h1>

          <p className="text-gray-600 mb-6">
            {message}
          </p>

          {status === 'success' && (
            <div className="bg-success-50 border border-success-200 rounded-lg p-4 mb-6">
              <p className="text-success-800 text-sm">
                Redirecting you to your dashboard in 3 seconds...
              </p>
            </div>
          )}

          {status === 'error' && email && (
            <div className="space-y-4">
              <button
                onClick={handleResendVerification}
                disabled={isResending}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend verification email
                  </>
                )}
              </button>
              
              <div className="text-sm text-gray-500">
                Or{' '}
                <button
                  onClick={() => navigate('/sign-in')}
                  className="text-brand-600 hover:text-brand-500 font-medium"
                >
                  go back to sign in
                </button>
              </div>
            </div>
          )}

          {status === 'error' && !email && (
            <button
              onClick={() => navigate('/sign-in')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
            >
              Go to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default VerifyEmailPage
